import { ChangeDetectionStrategy, Component, ElementRef, Input, Renderer2 } from '@angular/core';
import { GenNavigationUiComponent } from "../../footer-navigation/navigation-ui/navigation-ui.component";
import { Subscription } from "rxjs";
import { Router } from "@angular/router";
import { HamburgerMenuService, NavigationNode } from "@spartacus/storefront";
import { WindowRef } from "@spartacus/core";
import { distinctUntilChanged, map } from "rxjs/operators";
import { GenCustomerService } from 'src/app/generac/services/customer.service';
import { GenGoogleAnalyticsService } from 'src/app/generac/services/google-analytics.service';

@Component({
    selector: 'gen-header-navigation',
    templateUrl: './header-navigation.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GenHeaderNavigationComponent extends GenNavigationUiComponent {
  private customSubscriptions = new Subscription();
  private readonly WHOLE_HOME_ECOSYSTEM_CODE = 'whole-home-ecosystem';
  private readonly WHOLE_HOME_ECOSYSTEM_SUBMENU = 'ecosystem_submenu';

  @Input() isAllCategoriesVisible: boolean;

  override node: NavigationNode | null;
  isWrapperVisible = false;
  isAllCategoriesExpanded = false;
  activeCategorySlide = 1;
  isMobile: boolean;

  constructor(
    router: Router,
    protected _renderer: Renderer2,
    protected _elemRef: ElementRef,
    protected override hamburgerMenuService: HamburgerMenuService,
    protected override winRef: WindowRef,
    protected customerService: GenCustomerService,
    protected googleAnalyticsService: GenGoogleAnalyticsService,
  ) {
    super(router, _renderer, _elemRef, hamburgerMenuService, winRef);
  }

  override ngOnInit() {
    super.ngOnInit();
    this.subscribeToHamburgerMenuExpand();
    this.setCompanyNumber();
    this.subscribeToWindowResize();
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.customSubscriptions.unsubscribe();
  }

  onMouseClickAllCategories(categories: NavigationNode[]): void {
    if (this.isAllCategoriesExpanded) {
      this.deactivateAllCategories(categories);
      return;
    }
    this.isAllCategoriesExpanded = true;
    this.activeCategorySlide = 1;
  }

  deactivateAllCategories(categories: NavigationNode[]): void {
    this.deactivateCategories(categories);
    this.isAllCategoriesExpanded = false;
    this.activeCategorySlide = 1;
  }

  deactivateAllCategoriesByClickOutside(categories: NavigationNode[]): void {
    if (this.isMobile) {
      return;
    }
    this.deactivateAllCategories(categories);
  }

  override onMouseEnter(event: MouseEvent): void {
    this.isWrapperVisible = true;
  }

  override toggleOpen(event: UIEvent) {
    const currentTarget: Element = event.currentTarget as Element;
    if (this.winRef.nativeWindow?.innerWidth < 992) {
      const header = this._elemRef.nativeElement.closest('header')?.querySelector('.header');
      if (header) {
        this._renderer.setStyle(this._elemRef.nativeElement, '--account-panel-top',
          `${Math.max(0, header.getBoundingClientRect().bottom)}px`);
      }
    }
    if (!!currentTarget?.closest('li')?.classList.contains('is-open')) {
      this.back();
    } else {
      this.back();
      super.toggleOpen(event);
    }
  }

  closeMenu(): void {
    this.isWrapperVisible = false;
    this.isAllCategoriesExpanded = false;
    this.back();
  }

  onCategoryMouseEnter(categories: NavigationNode[], index: number, activeCategorySlide = 0) {
    if (this.isMobile || (!this.isMobile && activeCategorySlide >= 4)) {
      return;
    }
    categories.forEach((category, categoryIndex) => {
      if (category.isSelected && (index !== categoryIndex))
        category.isSelected = false
    });
    categories[index].isSelected = true;

    this.deactivateCategories(categories[index].children);
    this.activeCategorySlide = categories[index].children?.length ? activeCategorySlide + 1 : activeCategorySlide;
  }

  onCategoryMouseClick(categories: NavigationNode[], index: number, activeCategorySlide = 0) {
    if (!categories[index].children?.length) {
      return;
    }
    categories.forEach((category, categoryIndex) => {
      if (category.isSelected && (index !== categoryIndex))
        category.isSelected = false
    });
    categories[index].isSelected = true;

    this.deactivateCategories(categories[index].children);
    this.activeCategorySlide = activeCategorySlide + 1;
  }

  trackByItem(_: number, item: NavigationNode): string {
    return item?.title;
  }

  getActiveCategory(categories: any[]) {
    if (!categories) {
      return;
    }
    return categories.find(category => category.isSelected);
  }

  categoryBack(parentCategory: any, event: MouseEvent): void {
    this.deactivateCategories(parentCategory.children);
    this.activeCategorySlide = this.activeCategorySlide > 1 ? this.activeCategorySlide - 1 : 1;
    parentCategory.isSelected = false
    event.stopPropagation();
  }

  deactivateCategories(categories: any[], depth?: number): void {
    if (!categories?.length) {
      return;
    }
    categories?.forEach(category => {
      if ('isSelected' in category) {
        category.isSelected = false;
      }

      if (category.children && Array.isArray(category.children)) {
        this.deactivateCategories(category.children);
      }
    });
  }

  toggleHeader(): void {
    this.hamburgerMenuService.toggle();
    this.isWrapperVisible = false;
    this.isAllCategoriesExpanded = false;
  }

  sendEntryClickGaEvent(node: NavigationNode): void {
    if(node?.parentNodeId == this.WHOLE_HOME_ECOSYSTEM_CODE || node?.url?.includes(this.WHOLE_HOME_ECOSYSTEM_CODE)) {
      this.googleAnalyticsService.sendGaEvent('entry_click', {
        entry_source: this.WHOLE_HOME_ECOSYSTEM_SUBMENU,
        submenu_label: node?.title,
      });
    }
  }

  getNodeUrl(node: NavigationNode): string | string[] {
    if(node?.parentNodeId == this.WHOLE_HOME_ECOSYSTEM_CODE || node?.url?.includes(this.WHOLE_HOME_ECOSYSTEM_CODE)) {
      return node.url + `?entry_source=${this.WHOLE_HOME_ECOSYSTEM_SUBMENU}`
    }
    return node.url;
  }

  private subscribeToHamburgerMenuExpand(): void {
    this.customSubscriptions.add(
      this.hamburgerMenuService?.isExpanded
        .pipe(distinctUntilChanged())
        .subscribe((val) => !val && this.back())
    );
  }

  private setCompanyNumber(): void {
    const activeNode = this.node?.children[0]?.children?.find((x) => x.isActive);

    if (activeNode) {
      const companyNumber = activeNode.uid?.split('_')[0];
      localStorage.setItem('companyNumber', companyNumber);
      this.customerService.setCurrentB2bUnit(companyNumber);
    }
  }

  private subscribeToWindowResize(): void {
    this.customSubscriptions.add(
      this.winRef.resize$
        .pipe(
          distinctUntilChanged((_, current) => current.target.innerWidth > 992 === this.isMobile),
          map(win => win.target.innerWidth <= 992)
        ).subscribe(isMobile => this.isMobile = isMobile)
    )
  }

}
